import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import ProtectedRoute from '../../components/ProtectedRoute';
import { buildChangeDescription, recordAdminUpdate } from '../../utils/adminUpdates';

function AddTeam() {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [teams, setTeams] = useState([]);
  const [wrestlers, setWrestlers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedWrestlers, setSelectedWrestlers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [wrestlerSearchTerm, setWrestlerSearchTerm] = useState('');

  useEffect(() => {
    fetchWrestlers();
    fetchTeams();
  }, []);

  const fetchWrestlers = async () => {
    try {
      const q = query(collection(db, 'wrestlers'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const wrestlersData = querySnapshot.docs.map(entry => ({
        id: entry.id,
        ...entry.data()
      }));
      setWrestlers(wrestlersData);
    } catch (err) {
      console.error('Error fetching wrestlers:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const teamsSnapshot = await getDocs(collection(db, 'teams'));
      const teamsData = teamsSnapshot.docs.map(entry => ({
        id: entry.id,
        ...entry.data()
      }));
      setTeams(teamsData);
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const teamData = {
        name,
        members: selectedWrestlers,
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        const previousTeam = teams.find(item => item.id === editingId);
        await updateDoc(doc(db, 'teams', editingId), teamData);
        await recordAdminUpdate({
          type: 'Team',
          action: 'Modifica',
          title: name,
          description: buildChangeDescription('Team modificato', previousTeam, teamData, [
            { key: 'name', label: 'nome' },
            {
              key: 'members',
              label: 'membri',
              format: value => {
                const ids = Array.isArray(value) ? value : [];
                if (ids.length === 0) return 'nessuno';
                return ids
                  .map(id => wrestlers.find(wrestler => wrestler.id === id)?.name || id)
                  .join(', ');
              }
            }
          ]),
          entityType: 'teams',
          entityId: editingId
        });
        setMessage('Team modificato!');
      } else {
        const teamRef = await addDoc(collection(db, 'teams'), {
          ...teamData,
          createdAt: serverTimestamp()
        });
        await recordAdminUpdate({
          type: 'Team',
          action: 'Creazione',
          title: name,
          description: `Nuovo team creato: ${name}`,
          entityType: 'teams',
          entityId: teamRef.id
        });
        setMessage('Team aggiunto!');
      }

      await fetchTeams();
      resetForm();
    } catch (err) {
      console.error(err);
      setMessage('Errore nell\'operazione');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setName('');
    setSelectedWrestlers([]);
    setWrestlerSearchTerm('');
  };

  const handleEdit = (team) => {
    setEditingId(team.id);
    setShowCreateForm(true);
    setName(team.name);
    setSelectedWrestlers(team.members || []);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo team?')) {
      try {
        const team = teams.find(item => item.id === id);
        await deleteDoc(doc(db, 'teams', id));
        await recordAdminUpdate({
          type: 'Team',
          action: 'Eliminazione',
          title: team?.name || id,
          description: `Team eliminato: ${team?.name || id}`,
          entityType: 'teams',
          entityId: id
        });
        await fetchTeams();
        setMessage('Team eliminato!');
      } catch (err) {
        console.error(err);
        setMessage('Errore nell\'eliminazione');
      }
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredWrestlers = wrestlers.filter(wrestler =>
    wrestler.name.toLowerCase().includes(wrestlerSearchTerm.toLowerCase())
  );

  return (
    <ProtectedRoute>
      <div className="container admin-page py-4">

        {message && (
          <div className={`alert ${message.includes('Errore') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show mb-4`}>
            <i className={`bi ${message.includes('Errore') ? 'bi-exclamation-octagon' : 'bi-check-circle'} me-2`}></i>
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
          </div>
        )}

        <div className="admin-stats-grid mb-4">
          <div className="admin-stat-card">
            <span>Team Totali</span>
            <strong>{teams.length}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Wrestler Disponibili</span>
            <strong>{wrestlers.length}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Selezionati</span>
            <strong>{selectedWrestlers.length}</strong>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="card shadow-sm">
              <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
                <h2 className="h5 mb-0">
                  <i className="bi bi-people me-2"></i>
                  {editingId ? 'Modifica Team' : 'Nuovo Team'}
                </h2>
                {!editingId && (
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setShowCreateForm(v => !v)}
                  >
                    <i className={`bi ${showCreateForm ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`}></i>
                    {showCreateForm ? 'Chiudi' : 'Apri'}
                  </button>
                )}
              </div>

              {(editingId || showCreateForm) && (
                <div className="card-body">
                  <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-bold">Nome Team*</label>
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        placeholder="Inserisci nome del team"
                      />
                    </div>

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-bold">Membri selezionati</label>
                      {selectedWrestlers.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {selectedWrestlers.map(wrestlerId => {
                            const wrestler = wrestlers.find(item => item.id === wrestlerId);
                            return wrestler ? (
                              <span key={wrestlerId} className="badge bg-primary d-flex align-items-center">
                                {wrestler.name}
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-white p-0 ms-2"
                                  onClick={() => setSelectedWrestlers(selectedWrestlers.filter(id => id !== wrestlerId))}
                                  style={{ textDecoration: 'none' }}
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <div className="text-muted">Nessun wrestler selezionato.</div>
                      )}
                    </div>

                    <div className="admin-panel-soft mb-4">
                      <label className="form-label fw-bold">Cerca wrestler</label>
                      <div className="input-group mb-3">
                        <span className="input-group-text bg-white">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Cerca wrestler..."
                          value={wrestlerSearchTerm}
                          onChange={e => setWrestlerSearchTerm(e.target.value)}
                        />
                      </div>

                      <div className="row g-2">
                        {wrestlerSearchTerm.trim() === '' ? (
                          <div className="col-12">
                            <div className="alert alert-info mb-0">
                              <i className="bi bi-info-circle me-2"></i>
                              Digita almeno un carattere per cercare i wrestler.
                            </div>
                          </div>
                        ) : filteredWrestlers.length === 0 ? (
                          <div className="col-12">
                            <div className="alert alert-warning mb-0">
                              <i className="bi bi-exclamation-circle me-2"></i>
                              Nessun wrestler trovato.
                            </div>
                          </div>
                        ) : (
                          filteredWrestlers.map(wrestler => (
                            <div key={wrestler.id} className="col-md-6">
                              <label className="admin-panel d-flex align-items-center gap-2 h-100" htmlFor={`wrestler-${wrestler.id}`}>
                                <input
                                  className="form-check-input mt-0"
                                  type="checkbox"
                                  id={`wrestler-${wrestler.id}`}
                                  checked={selectedWrestlers.includes(wrestler.id)}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      setSelectedWrestlers([...selectedWrestlers, wrestler.id]);
                                    } else {
                                      setSelectedWrestlers(selectedWrestlers.filter(id => id !== wrestler.id));
                                    }
                                  }}
                                />
                                <span>{wrestler.name}</span>
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="d-flex gap-2 justify-content-end">
                      {(editingId || showCreateForm) && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={resetForm}
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Annulla
                        </button>
                      )}
                      <button type="submit" className="btn btn-primary">
                        <i className="bi bi-save me-2"></i>
                        {editingId ? 'Salva Modifiche' : 'Crea Team'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          <div className="col-lg-7">
            <div className="card shadow-sm admin-list-card">
              <div className="card-header bg-light py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <h3 className="h5 mb-0">
                    <i className="bi bi-list-ul me-2"></i>
                    Lista Team ({filteredTeams.length})
                  </h3>
                  <div className="input-group" style={{ maxWidth: '320px' }}>
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Cerca team..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="card-body">
                {teams.length === 0 ? (
                  <div className="alert alert-warning m-4">
                    <i className="bi bi-info-circle me-2"></i>
                    Nessun team registrato. Aggiungi il primo team!
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Nome Team</th>
                          <th>Membri</th>
                          <th style={{ width: '120px' }}>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTeams.map(team => (
                          <tr key={team.id}>
                            <td className="fw-bold">{team.name}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {team.members?.map(memberId => {
                                  const wrestler = wrestlers.find(w => w.id === memberId);
                                  return wrestler ? (
                                    <span key={memberId} className="badge">
                                      {wrestler.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEdit(team)}
                                  title="Modifica"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(team.id)}
                                  title="Elimina"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

export default AddTeam;
